"use client";

import {
	UTM_SESSION_COOKIE,
	UTM_FIRST_TOUCH_COOKIE,
	UTM_SESSION_TTL_SECONDS,
	decodeUtmCookieValue,
	toFirstTouchProperties,
	type UtmParams,
} from "./utm-attribution";
import {getMixpanelClientSync} from "../../mixpanel/lib/mixpanel-client";

function readCookie(name: string): string | undefined {
	const match = document.cookie
		.split("; ")
		.find((row) => row.startsWith(`${name}=`));
	if (!match) {
		return undefined;
	}
	return decodeURIComponent(match.slice(name.length + 1));
}

// Cookies scoped to the parent domain (set by the proxy) only apply that
// domain outside production, where the app runs on a different registrable
// domain (e.g. a Vercel preview URL) and the browser would reject it.
function parentDomainAttribute(): string {
	return window.location.hostname.endsWith("artofproblemsolving.com")
		? "; domain=.artofproblemsolving.com"
		: "";
}

export function getSessionUtmParams(): UtmParams | null {
	return decodeUtmCookieValue(readCookie(UTM_SESSION_COOKIE));
}

export function getFirstTouchUtmParams(): UtmParams | null {
	return decodeUtmCookieValue(readCookie(UTM_FIRST_TOUCH_COOKIE));
}

/**
 * Slide the session-attribution cookie's expiry forward without changing its
 * value. Call on every tracked client-side event so campaign attribution
 * survives ongoing activity within a session, not just page navigations
 * (which the proxy already refreshes on its own).
 *
 * Only for the session cookie: the first-touch cookie is written once by the
 * proxy and must never be touched from client JS, since Safari ITP caps
 * script-set cookies at 7 days regardless of the Max-Age given.
 */
export function touchSessionUtmCookie(): void {
	const value = readCookie(UTM_SESSION_COOKIE);
	if (!value) {
		return;
	}
	document.cookie = `${UTM_SESSION_COOKIE}=${encodeURIComponent(value)}; max-age=${UTM_SESSION_TTL_SECONDS}; path=/; secure; samesite=lax${parentDomainAttribute()}`;
}

/**
 * Registers session + first-touch UTM attribution as Mixpanel super
 * properties, so every subsequent .track() call (autocapture included)
 * carries them automatically via Mixpanel's own super-property propagation,
 * instead of every call site threading them in manually. Call once per page
 * load, after Mixpanel is initialized — not per event. We own the TTL via
 * our own cookie (30-min rolling), independent of Mixpanel's 365-day
 * default `cookie_expiration`.
 */
export function registerUtmSuperProperties(): void {
	const mixpanel = getMixpanelClientSync();
	if (!mixpanel) {
		return;
	}
	mixpanel.register({
		...getSessionUtmParams(),
		...toFirstTouchProperties(getFirstTouchUtmParams()),
	});
}
